// SEO & SECURITY ENHANCEMENT:
// This file has been refactored to use modern React Router v6 patterns,
// implement route-level security with a dedicated ProtectedRoute component,
// and add foundational SEO with dynamic titles via react-helmet-async.

import 'antd/dist/reset.css';
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { FilterProvider } from './context/FilterContext';
import { ToastProvider } from './context/ToastContext';
import { subscribeUser } from './pushNotifications';
import { Helmet } from 'react-helmet-async';
import { ConfigProvider } from 'antd'; // ADDED: Import ConfigProvider for Ant Design theming

// SECURITY ENHANCEMENT: Import the centralized ProtectedRoute component.
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header'; // Eagerly load Header
import { Footer } from './components/Footer'; // Eagerly load Footer
const Auth = lazy(() => import('./components/Auth'));
const ProductList = lazy(() => import('./components/ProductList'));
const OrderList = lazy(() => import('./components/OrderList'));
const ProductDetail = lazy(() => import('./components/ProductDetail/ProductDetail'));
const BuyerDashboard = lazy(() => import('./components/buyer-dashboard'));
const VendorDashboard = lazy(() => import('./components/vendor-dashboard'));
const AdminDashboard = lazy(() => import('./components/admin-dashboard'));
const Cart = lazy(() => import('./components/Cart'));
const Success = lazy(() => import('./components/Success'));
const Failure = lazy(() => import('./components/Failure'));
const ManageProduct = lazy(() => import('./components/ManageProduct'));
const EditProduct = lazy(() => import('./components/EditProduct'));
const CompleteProfile = lazy(() => import('./components/CompleteProfile'));
const AboutUs = lazy(() => import('./components/Footer/AboutUs'));
const PrivacyPolicy = lazy(() => import('./components/Footer/PrivacyPolicy'));
const Terms = lazy(() => import('./components/Footer/Terms'));
const FAQ = lazy(() => import('./components/Footer/FAQ'));
const AdminOrderManagement = lazy(() => import('./components/AdminOrderManagement'));
const VendorPayouts = lazy(() => import('./components/VendorPayouts'));
const AdminPayouts = lazy(() => import('./components/AdminPayouts'));
const AdminPayoutDashboard = lazy(() => import('./components/AdminPayoutDashboard'));
const AdminPayoutInvoiceDetail = lazy(() => import('./components/AdminPayoutInvoiceDetail'));
const AdminPayoutInvoiceList = lazy(() => import('./components/AdminPayoutInvoiceList'));
const AdminAccountingReports = lazy(() => import('./components/AdminAccountingReports'));
const RefundedOrders = lazy(() => import('./components/RefundedOrders'));
const AdminRefunds = lazy(() => import('./components/AdminRefunds'));
const AdminManualRefunds = lazy(() => import('./components/AdminManualRefunds'));
const OrderReturn = lazy(() => import('./components/OrderReturn'));
const VendorReturns = lazy(() => import('./components/VendorReturns'));
const AdminReturns = lazy(() => import('./components/AdminReturns'));
const DispatchReady = lazy(() => import('./components/DispatchReady'));
const NCMBranches = lazy(() => import('./components/NCMBranches'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop'));
const AdminVendorManagement = lazy(() => import('./components/AdminVendorManagement'));
const AdminOrdersSummary = lazy(() => import('./components/AdminOrdersSummary'));

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  // Android Native UI & Back Button Handling
  useEffect(() => {
    let backButtonListener = null;

    const setupNativeUI = async () => {
      if (window.Capacitor) {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        const { App } = await import('@capacitor/app');

        // 1. STATUS BAR SETUP - STRICT MODE
        try {
          // Make sure it is visible and NOT overlaying the WebView
          await StatusBar.show();
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setStyle({ style: Style.Light });
          await StatusBar.setBackgroundColor({ color: '#ffffff' });
        } catch (e) {
          console.warn('StatusBar plugin failed:', e);
        }

        // 2. BACK BUTTON HANDLING - NATIVE SYNC
        try {
          // Clean up any potential duplicates
          if (backButtonListener) {
            backButtonListener.remove();
          }

          backButtonListener = await App.addListener('backButton', ({ canGoBack }) => {
            // A. Priority: Close mobile menu if open
            if (window.isMobileMenuOpen) {
              window.dispatchEvent(new CustomEvent('closeMobileMenu'));
              return;
            }

            // B. Priority: Navigate back in history if possible
            // canGoBack is the native webview history check
            if (canGoBack) {
              window.history.back();
            } else {
              // C. Priority: Exit the app if at root
              App.exitApp();
            }
          });
        } catch (e) {
          console.warn('BackButton setup failed:', e);
        }
      }
    };

    setupNativeUI();

    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, []); // Run once on mount; listener uses native history check

// ADDED: Add body class for product pages to control header visibility
useEffect(() => {
  if (path.startsWith('/product/')) {
    document.body.classList.add('product-detail-page');
    document.body.classList.remove('auth-page');
    document.body.classList.remove('cart-page');
  } else if (path === '/auth') {
    document.body.classList.add('auth-page');
    document.body.classList.remove('product-detail-page');
    document.body.classList.remove('cart-page');
  } else if (path === '/cart') {
    document.body.classList.add('cart-page');
    document.body.classList.remove('product-detail-page');
    document.body.classList.remove('auth-page');
  } else {
    document.body.classList.remove('product-detail-page');
    document.body.classList.remove('auth-page');
    document.body.classList.remove('cart-page');
  }
  
  return () => {
    document.body.classList.remove('product-detail-page');
    document.body.classList.remove('auth-page');
    document.body.classList.remove('cart-page');
  };
}, [path]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      subscribeUser();
    }
  }, []);

  const shouldShowFooter =
    path === '/' ||
    path === '/auth' ||
    path === '/cart' ||
    path === '/about' ||
    path === '/privacy' ||
    path === '/terms' ||
    path === '/faq' ||
    path.startsWith('/product/');

  return (
    <>
      {/* SEO ENHANCEMENT: Set default title and description for the entire app. */}
      <Helmet
        titleTemplate="%s - YetiDeals"
        defaultTitle="YetiDeals: Unbeatable Deals in the Himalayas"
      >
        <meta name="description" content="YetiDeals is a multi-vendor e-commerce platform offering a wide range of products with unbeatable deals." />
      </Helmet>

      <ScrollToTop />
      <Header currentPath={path} />
      <main>
        <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<ProductList />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/product/:identifier" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/esewa_payment_success" element={<Success />} />
            <Route path="/esewa_payment_failed" element={<Failure />} />
            
            {/* Authenticated Routes */}
            <Route path="/complete-profile" element={<CompleteProfile />} />

            {/* Buyer Routes */}
            <Route path="/buyer-dashboard" element={<ProtectedRoute allowedRoles={['buyer']}><BuyerDashboard /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute allowedRoles={['buyer']}><OrderList /></ProtectedRoute>} />
            <Route path="/refunds" element={<ProtectedRoute allowedRoles={['buyer']}><RefundedOrders /></ProtectedRoute>} />
            <Route path="/order-return" element={<ProtectedRoute allowedRoles={['buyer']}><OrderReturn /></ProtectedRoute>} />

            {/* Vendor Routes */}
            <Route path="/vendor-dashboard/*" element={<ProtectedRoute allowedRoles={['vendor']}><VendorDashboard /></ProtectedRoute>} />
            <Route path="/edit-product/:productId" element={<ProtectedRoute allowedRoles={['vendor']}><EditProduct /></ProtectedRoute>} />
            <Route path="/vendor-payouts" element={<ProtectedRoute allowedRoles={['vendor']}><VendorPayouts /></ProtectedRoute>} />
            <Route path="/vendor-returns" element={<ProtectedRoute allowedRoles={['vendor']}><VendorReturns /></ProtectedRoute>} />

            {/* Admin Routes */}
            <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
<Route path="/admin-order-management/:orderId?" element={<ProtectedRoute allowedRoles={['admin']}><AdminOrderManagement /></ProtectedRoute>} />            <Route path="/admin/payouts-dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminPayoutDashboard /></ProtectedRoute>} />
            <Route path="/admin/payouts/:vendorId" element={<ProtectedRoute allowedRoles={['admin']}><AdminPayouts /></ProtectedRoute>} />
            <Route path="/admin/payout-invoices/:invoiceId" element={<ProtectedRoute allowedRoles={['admin']}><AdminPayoutInvoiceDetail /></ProtectedRoute>} />
            <Route path="/admin/payouts/:vendorId/invoices" element={<ProtectedRoute allowedRoles={['admin']}><AdminPayoutInvoiceList /></ProtectedRoute>} />
            <Route path="/admin/payout-invoices" element={<ProtectedRoute allowedRoles={['admin']}><AdminPayoutInvoiceList /></ProtectedRoute>} />
            <Route path="/admin/accounting-reports" element={<ProtectedRoute allowedRoles={['admin']}><AdminAccountingReports /></ProtectedRoute>} />
            <Route path="/admin-refunds" element={<ProtectedRoute allowedRoles={['admin']}><AdminRefunds /></ProtectedRoute>} />
            <Route path="/admin-returns" element={<ProtectedRoute allowedRoles={['admin']}><AdminReturns /></ProtectedRoute>} />
            <Route path="/admin-dashboard/dispatch-ready" element={<ProtectedRoute allowedRoles={['admin']}><DispatchReady /></ProtectedRoute>} />
            <Route path="/admin-dashboard/ncm-branches" element={<ProtectedRoute allowedRoles={['admin']}><NCMBranches /></ProtectedRoute>} />
            <Route path="/admin/manual-refunds" element={<ProtectedRoute allowedRoles={['admin']}><AdminManualRefunds /></ProtectedRoute>} />
            <Route path="/admin/vendor-management" element={<ProtectedRoute allowedRoles={['admin']}><AdminVendorManagement /></ProtectedRoute>} />
            <Route path="/admin-orders-summary" element={<ProtectedRoute allowedRoles={['admin']}><AdminOrdersSummary /></ProtectedRoute>} />

            {/* Fallback Route - could be a 404 page */}
            <Route path="*" element={<ProductList />} />
          </Routes>
        </Suspense>
      </main>
      {shouldShowFooter && <Footer />}
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <FilterProvider>
          <ToastProvider>
            {/* ADDED: ConfigProvider wraps the entire app for Ant Design theming */}
            {/* Memoize theme object to prevent unnecessary re-renders */}
            <ConfigProvider theme={React.useMemo(() => ({
              token: {
                colorPrimary: '#1890ff',
                borderRadius: 6,
                colorBgContainer: '#ffffff',
              },
              components: {
                Button: {
                  primaryShadow: '0 2px 0 rgba(0, 0, 0, 0.05)',
                },
                Input: {
                  activeBorderColor: '#1890ff',
                  hoverBorderColor: '#40a9ff',
                },
              },
            }), [])}>
              <AppContent />
            </ConfigProvider>
          </ToastProvider>
        </FilterProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
